import { create } from "zustand";

import type { WasteFilterDraft } from "@/hooks/use-waste-filters";

const STORAGE_KEY = "foodsight:waste-filters";

/** The filter subset that persists across waste pages (POS2-852): exactly the popover draft
 * fields. Page-specific state (page/sort) is not here, and neither is the date **preset**.
 *
 * **A preset never travels; explicit dates do** (POS2-1052/1053, matching the old platform).
 * "This week" / "Last week" are read relative to the page you set them on, and each page has its
 * own default window, so carrying one over means answering a question the next page didn't ask.
 * A date the user typed is unambiguous, so `date_from`/`date_to` persist — one bound or both —
 * and the destination reads them as a custom range.
 *
 * This replaced POS2-1049's `preset: DatePreset | null`, which tried to infer intent by comparing
 * the preset against the page default. That could not distinguish "chose the default" from "chose
 * nothing", so "Last week" carried while "This week" silently did not.
 *
 * An entry written before this change still has a `preset` key; it is simply ignored, since
 * nothing reads it. */
export type SharedWasteFilters = WasteFilterDraft;

interface WasteFiltersState {
  shared: SharedWasteFilters | null;
  /** Org the persisted filters belong to (POS2-967). `site_id` is org-specific, so a
   * consumer must ignore `shared` when this doesn't match the current org — otherwise a
   * platform admin switching org carries a foreign site_id into the next page. */
  orgId: string | null;
  setShared: (shared: SharedWasteFilters, orgId: string | null) => void;
  clearShared: () => void;
}

type Persisted = { shared: SharedWasteFilters; orgId: string | null };

function loadFromStorage(): { shared: SharedWasteFilters | null; orgId: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { shared: null, orgId: null };
    // Pre-POS2-967 entries were the bare SharedWasteFilters (no orgId wrapper); those
    // have no `.shared`, so they read as empty and are simply re-seeded — safe.
    const parsed = JSON.parse(raw) as Persisted;
    return { shared: parsed.shared ?? null, orgId: parsed.orgId ?? null };
  } catch {
    return { shared: null, orgId: null };
  }
}

/**
 * Cross-page persistence for the shared waste filters. sessionStorage (per-tab,
 * cleared on tab close) — filters should survive navigation, not the whole browser
 * session. Seeded and mirrored by `useWasteFilters`; the URL stays the live source
 * of truth within a page. Tagged with the org (POS2-967) so a stale site_id can't
 * cross orgs.
 */
export const useWasteFiltersStore = create<WasteFiltersState>((set) => {
  const init = loadFromStorage();
  return {
    shared: init.shared,
    orgId: init.orgId,
    setShared: (shared, orgId) => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ shared, orgId }));
      set({ shared, orgId });
    },
    clearShared: () => {
      sessionStorage.removeItem(STORAGE_KEY);
      set({ shared: null, orgId: null });
    },
  };
});
